import React from "react";

function ArrowButton(props) {
    const isDisabled = props.state === "disabled" || props.isLoading;
    return (
        <a 
            className={`page-link ${isDisabled ? 'disabled' : ''}`} 
            aria-label={props.type} 
            onClick={!isDisabled ? props.onClick : null}
            disabled={isDisabled}
        >
            <span aria-hidden="true">{props.value}</span>
        </a>
    );
}

class Pagination extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            activePage: 1,
            calculatedPages: null,
            visibleRange: [1, 10], // Initialize the default visible range as [1, 10]
        };
    }

    componentDidMount() {
        if (this.props.count > 0) {
            this.setState({
                calculatedPages: this.calculatePages(),
            }, this.adjustVisibleRange); // Adjust visible range after calculating pages
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.count && prevProps.count !== this.props.count) {
            this.setState({
                calculatedPages: this.calculatePages(),
            }, this.adjustVisibleRange); // Adjust visible range on count update
        }

        if (prevProps.refreshToken !== this.props.refreshToken) {
            this.setState({
                activePage: 1,
            }, this.adjustVisibleRange); // Reset visible range on refresh
        }
    }

    calculatePages() {
        return Math.ceil(this.props.count / this.props.limit);
    }

    adjustVisibleRange() {
        const { calculatedPages } = this.state;
        if (calculatedPages <= 10) {
            // If pages are less than or equal to 10, set the visible range to all pages
            this.setState({ visibleRange: [1, calculatedPages] });
        } else {
            // Default visible range for more than 10 pages
            this.setState({ visibleRange: [1, 10] });
        }
    }

    arrowClick(type) {
        if (this.props.isLoading) return;

        let nextPage = this.state.activePage;
        const { next, previous } = this.props.paging || {}; // Use `next` and `previous` from paging
        const { visibleRange } = this.state;

        // Use next/previous URLs if available
        if (type === "next" && next && nextPage < this.state.calculatedPages) {
            nextPage += 1;
            this.props.sendToPage(nextPage, true); // Use next URL for navigation
        } else if (type === "prev" && previous && nextPage > 1) {
            nextPage -= 1;
            this.props.sendToPage(nextPage, true); // Use prev URL for navigation
        } else if (type === "next" && nextPage < this.state.calculatedPages) {
            nextPage += 1;
            this.props.sendToPage(nextPage, false); // Use offset-based navigation
        } else if (type === "prev" && nextPage > 1) {
            nextPage -= 1;
            this.props.sendToPage(nextPage, false); // Use offset-based navigation
        }

        // Adjust the visible range of pages if necessary
        this.adjustRangeOnPageChange(nextPage, visibleRange);

        this.setState({ activePage: nextPage });
    }

    pageClick(page) {
        if (this.props.isLoading) return;

        const currentPage = this.state.activePage;
        const { next, previous } = this.props.paging || {};

        // Check if the clicked page is consecutive
        if (Math.abs(page - currentPage) === 1) {
            if (page > currentPage && next) {
                this.props.sendToPage(page, true); // Use next URL for consecutive navigation
            } else if (page < currentPage && previous) {
                this.props.sendToPage(page, true); // Use prev URL for consecutive navigation
            } else {
                this.props.sendToPage(page, false); // Use offset-based navigation
            }
        } else {
            const offset = (page - 1) * this.props.limit;
            this.props.sendToPage(page, false); // For non-consecutive page jumps, use offset
        }

        // Adjust the visible range of pages if necessary
        this.adjustRangeOnPageChange(page, this.state.visibleRange);

        this.setState({ activePage: page });
    }

    adjustRangeOnPageChange(page, visibleRange) {
        // Adjust the visible range of pages based on the current page
        if (page > visibleRange[1]) {
            // If we're moving past the current range, shift it forward
            this.setState({ visibleRange: [page, page + 9] });
        } else if (page < visibleRange[0]) {
            // If we're moving back, shift the range backward
            this.setState({ visibleRange: [page - 9, page] });
        }
    }

    generateButtons() {
        const { isLoading } = this.props;
        const { visibleRange, calculatedPages } = this.state;
        const pageNumbers = [];

        for (let i = visibleRange[0]; i <= Math.min(visibleRange[1], calculatedPages); i++) {
            const isActive = this.state.activePage === i ? "active" : "";
            const isDisabled = isLoading ? "disabled" : "";

            pageNumbers.push(
                <li key={i} className={`page-item ${isActive} ${isDisabled}`}>
                    <a 
                        className={`page-link ${isDisabled}`} 
                        onClick={() => !isLoading && this.pageClick(i)}
                        disabled={isLoading}
                    >
                        {i}
                    </a>
                </li>
            );
        }

        return pageNumbers;
    }

    render() {
        const { isLoading } = this.props;
        const prevState = this.state.activePage === 1 ? "disabled" : "active";
        const nextState = this.state.activePage === this.state.calculatedPages ? "disabled" : "active";

        if (this.state.calculatedPages === 0) {
            return null; // Return nothing if there are no pages to show
        }

        return (
            <ul className="pagination float-end">
                <li className="page-item">
                    <ArrowButton 
                        type="prev" 
                        value="«" 
                        onClick={() => this.arrowClick("prev")} 
                        state={prevState} 
                        isLoading={isLoading}
                    />
                </li>
                {this.generateButtons()}
                <li className="page-item">
                    <ArrowButton 
                        type="next" 
                        value="»" 
                        onClick={() => this.arrowClick("next")} 
                        state={nextState} 
                        isLoading={isLoading}
                    />
                </li>
            </ul>
        );
    }
}

Pagination.defaultProps = {
    limit: 10,
    count: 0,
    isLoading: false,
    paging: {}, 
};

export default Pagination;
